// What the wearer is actually receiving, as a bar.
//
// The silence warning tells an operator something is wrong after eight seconds. This tells them the
// instant it is right: speak, and the bar moves. It is the only feedback on this screen that does
// not require asking the person in distress whether they can hear you.
//
// It reads the PROCESSED stream, so it shows the signal after gain — what is transmitted, not what
// the microphone captured.

import { useTranslation } from 'react-i18next'

export interface MicLevelMeterProps {
  /** Peak amplitude of the last sample, 0-1. Null before the first one. */
  peak: number | null
  /** The operator has muted themselves — the bar must not imply they are being heard. */
  muted?: boolean
}

/** Speech peaks well below 1.0, so the bar is scaled to make ordinary talking fill most of it. */
const FULL_SCALE = 0.35

export function MicLevelMeter({ peak, muted = false }: MicLevelMeterProps) {
  const { t } = useTranslation('console')
  const pct = muted ? 0 : Math.min(100, Math.round(((peak ?? 0) / FULL_SCALE) * 100))

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-gray-400">
        {muted ? t('alerts.sos.call.muted', 'Your microphone is off') : t('alerts.sos.call.micLevel', 'Level')}
      </span>
      <div
        className="h-2 w-full overflow-hidden rounded bg-gray-700"
        role="meter"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('alerts.sos.call.micLevel', 'Level')}
        data-testid="sos-call-mic-level"
        data-level={pct}
      >
        <div
          className={`h-full transition-[width] duration-75 ${pct > 2 ? 'bg-green-500' : 'bg-gray-600'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}
